import styled, { css } from 'styled-components';
import { createListItemVariants } from 'components/List/shared/helpers';
import getTypeStyling from 'helpers/getTypeStyling';
import { actionWrapperStyling, ListItemSizes } from '../../shared/styles';
import type { ListItemType, Size } from '../../shared/types';

export const StyledListItem = styled.li<Pick<ListItemType, 'selected'> & Pick<ListItemType, 'size'>>`
    position: relative;
    display: flex;
    outline: none;
    line-height: 1;
    align-items: stretch;
    content-visibility: auto;
    ${({ size, theme }) => {
        if (size) return getTypeStyling((resolvedSize: Size) => ListItemSizes[resolvedSize].item)(size as Size);
        if ((theme as any).size)
            return getTypeStyling((resolvedSize: Size) => ListItemSizes[resolvedSize].item)((theme as any).size);
        return ListItemSizes.md.item;
    }}

    &:hover {
        background-color: ${({ theme }) => theme.ColorBackgroundHover};
    }

    &:focus-within {
        background-color: ${({ theme }) => theme.ColorBackgroundFocus};
    }

    &:active {
        background-color: ${({ theme }) => theme.ColorBackgroundPressed};
    }

    ${({ selected }) =>
        selected &&
        css`
            background-color: ${({ theme }) => theme.ColorBackgroundHover};
        `}
`;

export const SimpleWrapper = styled.span<Pick<ListItemType, 'size'>>`
    ${actionWrapperStyling}
    ${({ size, theme }) => {
        if (size)
            return getTypeStyling((resolvedSize: Size) => ListItemSizes[resolvedSize].actionWrapper)(size as Size);
        if ((theme as any).size)
            return getTypeStyling((resolvedSize: Size) => ListItemSizes[resolvedSize].actionWrapper)(
                (theme as any).size
            );
        return ListItemSizes.md.actionWrapper;
    }}
`;

const Content = styled.span<Pick<ListItemType, 'nowrap'>>`
    flex: 1 1 auto;
    min-width: 0;
    line-height: 1.5;
`;

export const BeforeWrapper = styled.span<Pick<ListItemType, 'size'>>`
    ${({ size, theme }) => {
        if (size) return getTypeStyling((resolvedSize: Size) => ListItemSizes[resolvedSize].beforeSlot)(size as Size);
        if ((theme as any).size)
            return getTypeStyling((resolvedSize: Size) => ListItemSizes[resolvedSize].beforeSlot)((theme as any).size);
        return ListItemSizes.md.beforeSlot;
    }}
`;

const AfterWrapper = styled.span<Pick<ListItemType, 'size'>>`
    min-width: 0;
    max-width: 50%;
    ${({ size, theme }) => {
        if (size) return getTypeStyling((resolvedSize: Size) => ListItemSizes[resolvedSize].afterSlot)(size as Size);
        if ((theme as any).size)
            return getTypeStyling((resolvedSize: Size) => ListItemSizes[resolvedSize].afterSlot)((theme as any).size);
        return ListItemSizes.md.afterSlot;
    }}
`;

function ListItem(props: ListItemType) {
    const {
        children,
        BeforeRenderer,
        AfterRenderer,
        WrapperRenderer = SimpleWrapper,
        size,
        selected,
        className,
        ...restProps
    } = props;
    const classNames = selected ? `selected ${className}` : className;
    const beforeContent = BeforeRenderer?.();
    const afterContent = AfterRenderer?.();

    const content = (
        <>
            {beforeContent && <BeforeWrapper size={size}>{beforeContent}</BeforeWrapper>}
            <Content>{children}</Content>
            {afterContent && <AfterWrapper size={size}>{afterContent}</AfterWrapper>}
        </>
    );

    return (
        <StyledListItem size={size} selected={selected} className={classNames} {...restProps}>
            <WrapperRenderer size={size}>{content}</WrapperRenderer>
        </StyledListItem>
    );
}

const listItemVariants = createListItemVariants(ListItem);

export default Object.assign(ListItem, listItemVariants);
